import { useMemo, useState, type ReactNode } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { ArrowDown, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { EmptyBlock, FetchingDot, SectionCard, Sparkline } from "../../components/primitives";
import { formatCount, formatMs, formatPercent } from "../../format";
import { useMonitoring } from "../../monitoring-context";
import { NEUTRAL } from "../../palette";
import type { MetricSeries, RouteStat } from "../../types";
import { LongPollHint, MethodChip, QueryBody } from "./bits";
import { coveredMinutes, isSlowByDesign, ROUTE_SORTS, SLOW_REQUEST_MS, sortRoutes, splitRoute, type RouteSort } from "./utils";

const PAGE = 25;

/** Per-route statistics of the board window: volume, failures, latency and the trend per bucket. */
export function RouteTable({ metrics }: { metrics: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const { window } = useMonitoring();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<RouteSort>("requests");
  const [showAll, setShowAll] = useState(false);

  return (
    <SectionCard
      title={t("monitoring.requests.routes.title")}
      description={t("monitoring.requests.routes.description", { window: t(`monitoring.window.long.${window}`) })}
      actions={<FetchingDot active={metrics.isFetching} />}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t("monitoring.requests.routes.search")}
            aria-label={t("monitoring.requests.routes.search")}
            className="h-8 pl-8 text-xs md:text-xs"
          />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-muted-foreground" id="route-sort-label">
            {t("monitoring.requests.routes.sortBy")}
          </span>
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={sort}
            onValueChange={(value) => value && setSort(value as RouteSort)}
            aria-labelledby="route-sort-label"
            className="flex-wrap justify-start gap-0.5"
          >
            {ROUTE_SORTS.map((key) => (
              <ToggleGroupItem key={key} value={key} className="h-8 px-2 text-xs">
                {t(`monitoring.requests.routes.sort.${key}`)}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
      </div>

      <QueryBody query={metrics} height={240}>
        {(data) => {
          const needle = query.trim().toLowerCase();
          const routes = sortRoutes(
            data.routes.filter((route) => !needle || route.route.toLowerCase().includes(needle)),
            sort,
          );
          const minutes = coveredMinutes(data, metrics.dataUpdatedAt);
          if (!data.routes.length) return <EmptyBlock>{t("monitoring.noData")}</EmptyBlock>;
          if (!routes.length) return <EmptyBlock>{t("monitoring.requests.routes.noMatch")}</EmptyBlock>;
          const shown = showAll ? routes : routes.slice(0, PAGE);
          return (
            <>
              <RouteRows routes={shown} minutes={minutes} sort={sort} />
              {routes.length > PAGE && (
                <div className="mt-3 flex justify-center">
                  <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowAll(!showAll)} aria-expanded={showAll}>
                    {showAll
                      ? t("monitoring.requests.routes.showFewer", { count: PAGE })
                      : t("monitoring.requests.routes.showAll", { count: routes.length })}
                  </Button>
                </div>
              )}
            </>
          );
        }}
      </QueryBody>
    </SectionCard>
  );
}

interface RouteView {
  route: RouteStat;
  method: string;
  path: string;
  slowByDesign: boolean;
  perMinute: number;
}

function RouteRows({ routes, minutes, sort }: { routes: RouteStat[]; minutes: number; sort: RouteSort }) {
  const { t, formatNumber } = useLocalization();
  // one variant in the DOM (the CSS breakpoint classes cover the first frame)
  const mobile = useIsMobile();
  const views = useMemo<RouteView[]>(
    () =>
      routes.map((route) => {
        const { method, path } = splitRoute(route.route);
        return { route, method, path, slowByDesign: isSlowByDesign(path), perMinute: route.n / minutes };
      }),
    [routes, minutes],
  );
  const rate = (value: number) => formatNumber(value, { maximumFractionDigits: value < 10 ? 1 : 0 });
  const sortMark = (key: RouteSort) =>
    sort === key ? <ArrowDown className="ml-0.5 inline h-3 w-3" aria-label={t("monitoring.requests.routes.sorted")} /> : null;

  return (
    <>
      {/* md+: table */}
      {!mobile && (
        <div className="hidden md:block">
          <Table className="text-xs">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-8 px-2">{t("monitoring.requests.col.route")}</TableHead>
                <TableHead className="h-8 px-2 text-right">{t("monitoring.requests.col.requests")}{sortMark("requests")}</TableHead>
                <TableHead className="hidden h-8 px-2 text-right lg:table-cell">{t("monitoring.requests.col.perMinute")}</TableHead>
                <TableHead className="h-8 px-2 text-right">4xx</TableHead>
                <TableHead className="h-8 px-2 text-right">5xx{sortMark("errors")}</TableHead>
                <TableHead className="h-8 px-2 text-right" title={t("monitoring.requests.col.errorRateHint")}>
                  {t("monitoring.requests.col.errorRate")}
                </TableHead>
                <TableHead className="hidden h-8 px-2 text-right xl:table-cell">{t("monitoring.series.p50")}</TableHead>
                <TableHead className="h-8 px-2 text-right">{t("monitoring.series.p95")}{sortMark("p95")}</TableHead>
                <TableHead className="h-8 px-2 text-right">{t("monitoring.requests.col.max")}{sortMark("max")}</TableHead>
                <TableHead className="hidden h-8 w-24 px-2 lg:table-cell">{t("monitoring.requests.col.trend")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {views.map(({ route, method, path, slowByDesign, perMinute }) => (
                <TableRow key={route.route}>
                  <TableCell className="px-2 py-1.5">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <MethodChip method={method} />
                      <span className="min-w-0 break-all font-mono text-[11px]">{path}</span>
                      {slowByDesign && <LongPollHint />}
                    </div>
                  </TableCell>
                  <Num>{formatCount(route.n)}</Num>
                  <Num className="hidden lg:table-cell">{rate(perMinute)}</Num>
                  <Num className={route.s4 > 0 ? "text-amber-300" : "text-muted-foreground"}>{formatCount(route.s4)}</Num>
                  <Num className={route.s5 > 0 ? "text-red-300" : "text-muted-foreground"}>{formatCount(route.s5)}</Num>
                  <Num className={route.error_rate > 0 ? "text-red-300" : "text-muted-foreground"}>
                    {formatPercent(route.error_rate, route.error_rate > 0 && route.error_rate < 0.01 ? 2 : 1)}
                  </Num>
                  <Num className="hidden xl:table-cell">{formatMs(route.p50)}</Num>
                  <Num className={latencyClass(route.p95, slowByDesign)}>{formatMs(route.p95)}</Num>
                  <Num className={latencyClass(route.max, slowByDesign)}>{formatMs(route.max)}</Num>
                  <TableCell className="hidden px-2 py-1.5 lg:table-cell">
                    <Sparkline values={route.series} bars color={NEUTRAL.strong} height={20} className="w-24" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* below md: cards */}
      {mobile && (
        <ul className="flex flex-col gap-2 md:hidden">
          {views.map(({ route, method, path, slowByDesign, perMinute }) => (
            <li key={route.route} className="rounded-md border px-3 py-2.5">
              <div className="flex flex-wrap items-center gap-1.5">
                <MethodChip method={method} />
                <span className="min-w-0 break-all font-mono text-[11px]">{path}</span>
                {slowByDesign && <LongPollHint />}
              </div>
              <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-1.5 text-[11px]">
                <Stat label={t("monitoring.requests.col.requests")} value={formatCount(route.n)} />
                <Stat label={t("monitoring.requests.col.perMinute")} value={rate(perMinute)} />
                <Stat
                  label={t("monitoring.requests.col.errorRate")}
                  value={formatPercent(route.error_rate, route.error_rate > 0 && route.error_rate < 0.01 ? 2 : 1)}
                  className={route.error_rate > 0 ? "text-red-300" : undefined}
                />
                <Stat label="4xx" value={formatCount(route.s4)} className={route.s4 > 0 ? "text-amber-300" : undefined} />
                <Stat label="5xx" value={formatCount(route.s5)} className={route.s5 > 0 ? "text-red-300" : undefined} />
                <Stat label={t("monitoring.series.p50")} value={formatMs(route.p50)} />
                <Stat label={t("monitoring.series.p95")} value={formatMs(route.p95)} className={latencyClass(route.p95, slowByDesign)} />
                <Stat label={t("monitoring.requests.col.max")} value={formatMs(route.max)} className={latencyClass(route.max, slowByDesign)} />
              </dl>
              <Sparkline values={route.series} bars color={NEUTRAL.strong} height={20} className="mt-2" />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

const latencyClass = (value: number | null | undefined, slowByDesign: boolean) =>
  !slowByDesign && (value ?? 0) >= SLOW_REQUEST_MS ? "text-amber-300" : undefined;

function Num({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <TableCell className={cn("whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums", className)}>
      {children}
    </TableCell>
  );
}

function Stat({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn("font-mono tabular-nums", className)}>{value}</dd>
    </div>
  );
}
